import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Connector, CONNECTOR_TYPE_LABELS } from "@/types";
import { getPartnerTypeColor } from "./partnerMarkers";
import { MapPin, Phone, ArrowRight, Star } from "lucide-react";
import { format } from "date-fns";

interface PartnerMapPopupProps {
  partner: Connector;
}

/**
 * Brief partner summary shown inside a map pin's popup, with a link through to
 * the full detail page.
 */
export function PartnerMapPopup({ partner }: PartnerMapPopupProps) {
  const navigate = useNavigate();
  const isDeactivated = partner.isActive === false;
  const locationLine = [partner.city, partner.state]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="min-w-[210px] max-w-[260px] space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-sm leading-tight break-words">
            {partner.name}
            {partner.isFavorite && (
              <Star className="inline h-3.5 w-3.5 ml-1 -mt-0.5 fill-amber-400 text-amber-400" />
            )}
          </p>
          {partner.businessName && (
            <p className="text-xs text-muted-foreground break-words">
              {partner.businessName}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <Badge
          variant="secondary"
          className="text-[10px] font-medium border-0 text-white"
          style={{ backgroundColor: getPartnerTypeColor(partner.type) }}
        >
          {CONNECTOR_TYPE_LABELS[partner.type] ?? partner.type}
        </Badge>
        {isDeactivated && (
          <Badge variant="destructive" className="text-[10px]">
            Deactivated
          </Badge>
        )}
        {partner.kycVerified && (
          <Badge variant="outline" className="text-[10px]">
            KYC
          </Badge>
        )}
      </div>

      {locationLine && (
        <p className="text-xs text-muted-foreground flex items-start gap-1">
          <MapPin className="h-3.5 w-3.5 mt-px flex-shrink-0" />
          <span className="break-words">{locationLine}</span>
        </p>
      )}

      {partner.whatsappNumber && (
        <a
          href={`tel:${partner.whatsappNumber}`}
          className="text-xs text-primary flex items-center gap-1 hover:underline"
        >
          <Phone className="h-3.5 w-3.5 flex-shrink-0" />
          {partner.whatsappNumber}
        </a>
      )}

      <div className="text-xs text-muted-foreground pt-0.5">
        <span>{partner.totalVisits ?? 0} visits</span>
        {partner.lastVisitDate && (
          <span> &middot; last {format(partner.lastVisitDate, "d MMM yyyy")}</span>
        )}
      </div>

      <Button
        size="sm"
        className="w-full h-8 text-xs"
        onClick={() => navigate(`/tools/channel-partners/${partner.id}`)}
      >
        View Details
        <ArrowRight className="h-3.5 w-3.5 ml-1" />
      </Button>
    </div>
  );
}
